import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send,
  UserRoundCog,
  Hand,
  Play,
  X,
  Clock,
  Settings,
  AlertCircle,
  ArrowLeft,
  User,
  Star,
  Ban,
  ShieldBan,
  ShieldCheck,
  Loader2,
} from 'lucide-react';
import { parseDisplayName } from '@/lib/utils';
import type { ConversationStatus } from '@/types/outreach';
import type { InboxHooks, InboxPlatformConfig, InboxStore } from '../types';
import { GenericMessageBubble } from '../GenericMessageBubble';
import styles from './GenericChatView.module.css';

interface Props {
  hooks: InboxHooks;
  store: InboxStore;
  platform: InboxPlatformConfig;
}

function formatWindowExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'Closed';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${mins}m`;
}

export function GenericChatView({ hooks, store, platform }: Props) {
  const { selectedConversationId, toggleAgentConfig, clearSelectedConversationId } = store;
  const [input, setInput] = useState('');
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(0);

  // iOS keyboard handling: when virtual keyboard opens, the visualViewport shrinks
  // but the layout viewport stays the same. We listen for resize events and adjust
  // the container height so the compose bar stays visible above the keyboard.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let resizeRAF = 0;
    const onResize = () => {
      cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(() => {
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const availableHeight = vv.height - rect.top + vv.offsetTop;
        if (availableHeight < container.offsetHeight - 50) {
          container.style.height = `${availableHeight}px`;
          messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        } else {
          container.style.height = '';
        }
      });
    };

    const onScroll = () => {
      if (vv.offsetTop > 0) {
        window.scrollTo(0, 0);
      }
    };

    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onScroll);

    return () => {
      cancelAnimationFrame(resizeRAF);
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onScroll);
      if (containerRef.current) {
        containerRef.current.style.height = '';
      }
    };
  }, []);

  const { data: conversation } = hooks.useConversationDetail(selectedConversationId);
  const { data: messagesData, isLoading: messagesLoading } =
    hooks.useConversationMessages(selectedConversationId);
  const sendMessage = hooks.useSendAdminMessage();
  const updateStatus = hooks.useUpdateConversationStatus();
  const markRead = hooks.useMarkConversationRead();
  const toggleStar = hooks.useToggleStar?.();
  const blacklistMutation = hooks.useBlacklistConversation?.();
  const unblacklistMutation = hooks.useUnblacklistConversation?.();

  const messages = messagesData?.results ?? [];

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  // Mark as read when selecting conversation
  useEffect(() => {
    if (selectedConversationId && conversation && conversation.unreadCount > 0) {
      markRead.mutate(selectedConversationId);
    }
  }, [selectedConversationId, conversation?.unreadCount]);

  const handleSend = useCallback(() => {
    if (!input.trim() || !selectedConversationId) return;
    sendMessage.mutate(
      { conversationId: selectedConversationId, content: input.trim() },
      { onSuccess: () => setInput('') },
    );
  }, [input, selectedConversationId, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStatusChange = (newStatus: ConversationStatus) => {
    if (!selectedConversationId) return;
    updateStatus.mutate({ conversationId: selectedConversationId, status: newStatus });
  };

  const PlatformIcon = platform.icon;

  if (!selectedConversationId) {
    return (
      <div className={styles.empty}>
        <PlatformIcon className={styles.emptyIcon} />
        <h3 className={styles.emptyTitle}>{platform.label} Inbox</h3>
        <p className={styles.emptyText}>Select a conversation to start chatting</p>
        <button onClick={toggleAgentConfig} className={styles.emptySettingsBtn}>
          <Settings className={styles.emptySettingsIcon} />
          AI Agent Settings
        </button>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className={styles.empty}>
        <div className={styles.spinner} />
      </div>
    );
  }

  const displayName =
    conversation.customerName ||
    (conversation.place ? parseDisplayName(conversation.place.displayName)?.text : null) ||
    conversation.primaryId;

  const isWindowOpen = conversation.isWindowOpen;
  const hasPic = !!conversation.profilePicUrl;
  const isStarred = conversation.isStarred ?? false;

  const resumeBtnClass =
    platform.resumeAiBtnVariant === 'blue'
      ? `${styles.actionBtn} ${styles.actionBtnBlue}`
      : `${styles.actionBtn} ${styles.actionBtnGreen}`;

  return (
    <div ref={containerRef} className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button
          onClick={clearSelectedConversationId}
          className={styles.backBtn}
          title="Back to conversations"
        >
          <ArrowLeft className={styles.backIcon} />
        </button>

        {/* Profile picture (shown when available — Instagram / Messenger) */}
        {hasPic || platform.name !== 'whatsapp' ? (
          <div className={styles.headerAvatar}>
            {hasPic ? (
              <img
                src={conversation.profilePicUrl!}
                alt={displayName || 'User'}
                className={styles.headerAvatarImg}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  (e.currentTarget.nextElementSibling as HTMLElement | null)?.removeAttribute(
                    'style',
                  );
                }}
              />
            ) : null}
            <div
              className={styles.headerAvatarFallback}
              style={{ display: hasPic ? 'none' : 'flex' }}
            >
              <User className={styles.headerAvatarIcon} />
            </div>
          </div>
        ) : null}

        <div className={styles.headerInfo}>
          <h3 className={styles.headerName}>{displayName}</h3>
          <span className={styles.headerPrimaryId}>{conversation.primaryId}</span>
          {conversation.secondaryId && (
            <span className={styles.headerSecondaryId}>{conversation.secondaryId}</span>
          )}
          {conversation.place && (
            <span className={styles.headerBusiness}>
              {parseDisplayName(conversation.place.displayName)?.text}
            </span>
          )}
        </div>

        <div className={styles.headerActions}>
          {toggleStar && (
            <button
              type="button"
              className={`${styles.actionBtn} ${isStarred ? styles.actionBtnStarActive : ''}`}
              aria-label={isStarred ? 'Unstar conversation' : 'Star conversation'}
              title={isStarred ? 'Unstar conversation' : 'Star conversation'}
              onClick={() => {
                if (selectedConversationId) {
                  toggleStar.mutate({
                    conversationId: selectedConversationId,
                    isStarred: !isStarred,
                  });
                }
              }}
            >
              <Star
                className={styles.actionIcon}
                style={
                  isStarred
                    ? { fill: 'var(--color-warning)', color: 'var(--color-warning)' }
                    : undefined
                }
              />
            </button>
          )}
          {blacklistMutation && (
            <>
              {!conversation?.isPhoneBlacklisted ? (
                showBlockConfirm ? (
                  <div
                    className={styles.blockConfirmStrip}
                    role="group"
                    aria-label="Confirm block this number"
                  >
                    <button
                      type="button"
                      className={styles.blockConfirmBtn}
                      onClick={() => {
                        if (selectedConversationId && !blacklistMutation.isPending) {
                          blacklistMutation.mutate({ conversationId: selectedConversationId });
                          setShowBlockConfirm(false);
                        }
                      }}
                      disabled={blacklistMutation.isPending}
                      aria-busy={blacklistMutation.isPending}
                    >
                      {blacklistMutation.isPending ? (
                        <>
                          <Loader2 size={12} className={styles.inlineSpinner} aria-hidden="true" />
                          Blocking…
                        </>
                      ) : (
                        'Block'
                      )}
                    </button>
                    <button
                      type="button"
                      className={styles.cancelConfirmBtn}
                      onClick={() => setShowBlockConfirm(false)}
                      disabled={blacklistMutation.isPending}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.headerActionBtn}
                    onClick={() => setShowBlockConfirm(true)}
                    aria-label="Block this number"
                    title="Block this number from receiving messages"
                  >
                    <Ban size={18} aria-hidden="true" />
                  </button>
                )
              ) : (
                <button
                  type="button"
                  className={`${styles.headerActionBtn} ${styles.headerActionBtnBlocked}`}
                  onClick={() => {
                    if (selectedConversationId && !unblacklistMutation?.isPending) {
                      unblacklistMutation?.mutate({ conversationId: selectedConversationId });
                    }
                  }}
                  aria-label={
                    unblacklistMutation?.isPending
                      ? 'Unblocking this number'
                      : 'Unblock this number'
                  }
                  title="Remove from blacklist"
                  disabled={unblacklistMutation?.isPending}
                  aria-busy={unblacklistMutation?.isPending}
                >
                  {unblacklistMutation?.isPending ? (
                    <Loader2 size={18} className={styles.inlineSpinner} aria-hidden="true" />
                  ) : (
                    <ShieldCheck size={18} aria-hidden="true" />
                  )}
                </button>
              )}
            </>
          )}
          <div className={styles.windowIndicator}>
            <Clock className={styles.windowIcon} />
            <span className={isWindowOpen ? styles.windowOpen : styles.windowClosed}>
              {formatWindowExpiry(conversation.windowExpiresAt)}
            </span>
          </div>
          {conversation.status === 'ACTIVE' && (
            <button
              onClick={() => handleStatusChange('HANDOFF')}
              className={styles.actionBtn}
              title="Take over from AI"
            >
              <Hand className={styles.actionIcon} />
              <span>Take Over</span>
            </button>
          )}
          {conversation.status === 'HANDOFF' && (
            <button
              onClick={() => handleStatusChange('ACTIVE')}
              className={resumeBtnClass}
              title="Resume AI agent"
            >
              <Play className={styles.actionIcon} />
              <span>Resume AI</span>
            </button>
          )}
          {conversation.status !== 'CLOSED' && (
            <button
              onClick={() => handleStatusChange('CLOSED')}
              className={`${styles.actionBtn} ${styles.actionBtnRed}`}
              title="Close conversation"
            >
              <X className={styles.actionIcon} />
            </button>
          )}
          <button onClick={toggleAgentConfig} className={styles.actionBtn} title="Agent settings">
            <Settings className={styles.actionIcon} />
          </button>
        </div>
      </div>

      {/* Handoff banner */}
      {conversation.status === 'HANDOFF' && (
        <div className={styles.handoffBanner}>
          <UserRoundCog className={styles.handoffIcon} />
          <span>AI paused — you are managing this conversation</span>
        </div>
      )}

      {/* Messages */}
      <div className={styles.messages}>
        {messagesLoading ? (
          <div className={styles.messageSkeletons}>
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className={`${styles.msgSkeleton} ${i % 2 === 0 ? styles.msgSkeletonLeft : styles.msgSkeletonRight}`}
              >
                <div
                  className={styles.msgSkeletonBubble}
                  style={{ width: `${45 + (i % 3) * 15}%` }}
                />
              </div>
            ))}
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <GenericMessageBubble key={msg.id} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Compose */}
      <div className={styles.compose}>
        {conversation?.isPhoneBlacklisted ? (
          <div className={styles.blacklistedBanner} role="status" aria-live="polite">
            <ShieldBan size={20} aria-hidden="true" />
            <div className={styles.blacklistedBannerText}>
              <span className={styles.blacklistedBannerTitle}>This number is blocked</span>
              <span className={styles.blacklistedBannerSub}>
                {conversation.phoneBlacklistReason || 'No reason given'}
              </span>
            </div>
            {unblacklistMutation && (
              <button
                type="button"
                className={styles.unblockBtn}
                onClick={() => {
                  if (selectedConversationId && !unblacklistMutation.isPending) {
                    unblacklistMutation.mutate({ conversationId: selectedConversationId });
                  }
                }}
                disabled={unblacklistMutation.isPending}
                aria-busy={unblacklistMutation.isPending}
              >
                {unblacklistMutation.isPending ? (
                  <>
                    <Loader2 size={12} className={styles.inlineSpinner} aria-hidden="true" />
                    Unblocking…
                  </>
                ) : (
                  'Unblock'
                )}
              </button>
            )}
          </div>
        ) : !isWindowOpen ? (
          <div className={styles.windowExpired}>
            <AlertCircle className={styles.windowExpiredIcon} />
            <span>{platform.windowExpiredText}</span>
          </div>
        ) : (
          <div className={styles.composeRow}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className={styles.composeInput}
              rows={1}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sendMessage.isPending}
              className={styles.sendBtn}
            >
              <Send className={styles.sendIcon} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
